import React, { useEffect, useState } from 'react';
import {
    Button, Collapse, DatePicker, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Radio, Select, Space, Switch, Table, Tag,
    Tooltip, Typography, message,
} from 'antd';
import {
    PlusOutlined, ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined, EditOutlined, CopyOutlined, DeleteOutlined,
    SendOutlined, HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import couponService from '../../services/couponService';
import { formatMoney } from '../../../shared/utils/money';
import { CAMPAIGN_THEMES, CampaignMotif, themeVars } from '../../../shared/campaign/campaignThemes';
import '../../../user/components/couponTicket.css';
import TicketValue from '../../../shared/campaign/TicketValue'; // same ticket look as the user console (theme preview)

const { Text } = Typography;
const { RangePicker } = DatePicker;
const LANGS = [['zh-CN', '简体中文'], ['zh-TW', '繁體中文'], ['es', 'Español'], ['ja', '日本語'], ['ko', '한국어'], ['ms', 'Bahasa Melayu'], ['ru', 'Русский']];
const STATE_COLOR = { draft: 'default', scheduled: 'blue', live: 'green', ended: 'default', stopped: 'red' };
const STATE_LABEL = { draft: 'Draft', scheduled: 'Upcoming', live: 'In progress', ended: 'Ended', stopped: 'Stopped', active: 'In progress' };
const fmt = (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-');

const offerText = (c, t) => {
    if (!c) return '';
    if (c.type === 'percent') return t('{{value}}% off', { value: Number(c.value) });
    if (c.type === 'fixed') return t('{{value}} off', { value: formatMoney(c.value, c.currency) });
    return c.name;
};

/** Theme preview: the user banner + ticket in that theme (same CSS as the console). */
const ThemePreview = ({ t, theme, name, offer, code, mode, coupon }) => {
    const v = themeVars(theme);
    return (
        <div className="cph-preview">
            <div className="cpb" style={v}>
                <CampaignMotif theme={theme} size={88} className="cpb-motif" />
                <div className="cpb-main">
                    <span className="cpb-name"><CampaignMotif theme={theme} size={16} />{name || t('Campaign')}</span>
                    <span className="cpb-offer">{offer || t('{{value}}% off', { value: 20 })}</span>
                    {mode === 'wallet' ? <span className="cpb-sub">{t('Your coupon is in My coupons')}</span> : null}
                </div>
                <div className="cpb-side">
                    <span className="cpb-count">{t('Ends in {{n}} days', { n: 3 })}</span>
                    {mode !== 'wallet' && code ? <span className="cpb-code" style={{ paddingRight: 12 }}>{code}</span> : null}
                    <Button className="cpb-cta" size="small">{t('Use now')}</Button>
                </div>
            </div>
            <div className="cpt cpt--themed" style={{ ...v, maxWidth: 420 }}>
                <div className="cpt-stub">
                    <CampaignMotif theme={theme} size={30} className="cpt-motif" />
                    <span className="cpt-campaign">{name || t('Campaign')}</span>
                    <TicketValue c={coupon || { type: 'percent', value: 20 }} t={t} />
                </div>
                <div className="cpt-body">
                    <div className="cpt-name">{name || t('Campaign')}</div>
                    <div className="cpt-scope">{t('All products')}</div>
                    {code ? <span className="cpt-code">{code}</span> : null}
                </div>
            </div>
        </div>
    );
};

/** Upcoming holidays (~12 months): one-click "Create" from the preset. */
const HolidayStrip = ({ t, holidays, onCreate }) => (
    <div className="cph-strip" role="list">
        {holidays.map((h) => (
            <div key={`${h.key}${h.year}`} className="cph-card" style={themeVars(h.theme)} role="listitem">
                <CampaignMotif theme={h.theme} size={30} />
                <span className="cph-name">{h.name} {h.year}</span>
                <span className="cph-date">{dayjs(h.date).format('YYYY-MM-DD')}{h.approximate ? ' ≈' : ''}</span>
                {h.campaign
                    ? <Tag>{`#${h.campaign.id} · ${t(STATE_LABEL[h.campaign.status] || h.campaign.status)}`}</Tag>
                    : <Button size="small" onClick={() => onCreate(h)}>{t('Create')}</Button>}
            </div>
        ))}
    </div>
);

/** 节日活动：列表 + 未来节日 + 表单（主题预览）+ 开启/停止/复制/发券预览。 */
const CouponCampaigns = ({ t, couponOptions, productTypes = [], onChanged }) => {
    const [rows, setRows] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState(null);
    const [form] = Form.useForm();
    const theme = Form.useWatch('theme', form) || 'default';
    const mode = Form.useWatch('mode', form) || 'code';
    const name = Form.useWatch('name', form);
    const newCoupon = Form.useWatch('new_coupon', form);
    const linked = Form.useWatch('coupon_ids', form);
    const bannerText = Form.useWatch('banner_text', form);

    const load = async () => {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([couponService.getCampaigns(), couponService.getHolidays(12)]);
            if (a.success) setRows(a.data || []);
            if (b.success) setHolidays(b.data || []);
        } catch (e) {
            message.error(e.response?.data?.error || e.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const fill = (v) => {
        form.resetFields();
        form.setFieldsValue({
            mode: 'code', theme: 'default', recurring: false, include_new_users: false, coupon_ids: [], ...v,
            window: v.starts_at ? [dayjs(v.starts_at), dayjs(v.ends_at)] : undefined,
            name_i18n: v.name_i18n && !Array.isArray(v.name_i18n) ? v.name_i18n : {},
            banner_i18n: v.banner_i18n && !Array.isArray(v.banner_i18n) ? v.banner_i18n : {},
        });
    };
    const openNew = () => { setEditing({}); fill({ new_coupon: { type: 'percent', value: 20 } }); };
    const openPreset = async (h) => {
        const r = await couponService.getCampaignPreset(h.key, h.year);
        if (!r.success) return message.error(r.error);
        setEditing({ preset: h });
        fill({ ...r.data, recurring: !!r.data.recurring });
        return null;
    };
    const openEdit = (r) => { setEditing(r); fill({ ...r, new_coupon: undefined }); };

    const submit = async () => {
        const v = await form.validateFields();
        const nc = v.new_coupon && v.new_coupon.code ? v.new_coupon : null;
        const payload = {
            id: editing?.id, name: v.name, name_i18n: v.name_i18n, holiday_key: v.holiday_key || null, theme: v.theme, year: v.year,
            starts_at: v.window[0].format('YYYY-MM-DD HH:mm:ss'), ends_at: v.window[1].format('YYYY-MM-DD HH:mm:ss'),
            mode: v.mode, coupon_ids: v.coupon_ids || [], include_new_users: !!v.include_new_users, recurring: !!v.recurring,
            banner_text: v.banner_text, banner_i18n: v.banner_i18n, new_coupon: nc,
        };
        setSaving(true);
        try {
            const r = await couponService.saveCampaign(payload);
            if (!r.success) return message.error(r.error);
            message.success(t('Saved'));
            setEditing(null);
            load();
            if (nc) onChanged?.();
        } catch (e) {
            message.error(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
        return null;
    };

    const act = async (action, r, extra) => {
        try {
            const res = await couponService.campaignAction(action, r.id, extra);
            res.success ? message.success(res.message || t('Done')) : message.error(res.error);
        } catch (e) {
            message.error(e.response?.data?.error || e.message);
        }
        load();
        onChanged?.();
    };

    /** Start: wallet campaigns confirm with the number of users first. */
    const start = async (r) => {
        if (r.mode !== 'wallet') return act('start', r);
        const p = await couponService.previewCampaign(r.id);
        const n = p.success ? p.data.count : '?';
        Modal.confirm({
            title: t('Start campaign'),
            content: t('{{n}} users will get the coupon (batches every 5 minutes from the start time).', { n }),
            onOk: () => act('start', r),
        });
        return null;
    };
    const issueNow = async (r) => {
        const p = await couponService.previewCampaign(r.id);
        const n = p.success ? p.data.count : '?';
        Modal.confirm({ title: t('Issue now'), content: t('Issue to {{n}} users now?', { n }), onOk: () => act('issue', r) });
    };

    const codesOf = (r) => (r.coupons || []).map((c) => (
        <Tooltip key={c.id} title={`${c.name} · ${offerText(c, t)}${c.status !== 'active' ? ` · ${t('Disabled')}` : ''}`}>
            <Tag color={c.status === 'active' ? 'blue' : 'default'}>{c.code}</Tag>
        </Tooltip>
    ));

    const columns = [
        {
            title: t('Campaign'), key: 'n', render: (_, r) => (
                <Space size={8}>
                    <span className="cps" style={themeVars(r.theme)} />
                    <span>
                        <div>{r.name}{r.recurring ? <Tag style={{ marginLeft: 6 }}>{t('Every year')}</Tag> : null}</div>
                        {r.last_error ? <Text type="danger" style={{ fontSize: 12 }}>{r.last_error}</Text> : null}
                    </span>
                </Space>
            ),
        },
        { title: t('Window'), key: 'w', render: (_, r) => <span style={{ whiteSpace: 'nowrap' }}>{fmt(r.starts_at)} → {fmt(r.ends_at)}</span> },
        { title: t('Mode'), dataIndex: 'mode', render: (v) => <Tag color={v === 'wallet' ? 'purple' : 'cyan'}>{t(v === 'wallet' ? 'Wallet' : 'Public code')}</Tag> },
        { title: t('Coupons'), key: 'c', render: (_, r) => <Space size={2} wrap>{codesOf(r)}</Space> },
        { title: t('Status'), dataIndex: 'state', render: (v) => <Tag color={STATE_COLOR[v]}>{t(STATE_LABEL[v] || v)}</Tag> },
        {
            title: <Tooltip title={t('Issued / used / checkouts / discount')}>{t('Stats')}</Tooltip>, key: 's',
            render: (_, r) => (r.stats ? (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {r.mode === 'wallet' ? `${r.stats.issued} / ${r.stats.used} · ` : ''}
                    {r.stats.redemptions} · {formatMoney(r.stats.discount)}
                </span>
            ) : '-'),
        },
        {
            title: '', key: 'a', render: (_, r) => (
                <Space size={0} wrap>
                    {r.status !== 'active' && r.state !== 'ended' ? (
                        <Tooltip title={t('Start')}><Button type="link" size="small" icon={<PlayCircleOutlined />} onClick={() => start(r)} /></Tooltip>
                    ) : null}
                    {r.status === 'active' && r.state !== 'ended' ? (
                        <Popconfirm title={r.mode === 'code' ? t('Stop and disable the code?') : t('Stop issuing? Issued coupons stay usable.')} onConfirm={() => act('stop', r)}>
                            <Tooltip title={t('Stop')}><Button type="link" size="small" danger icon={<PauseCircleOutlined />} /></Tooltip>
                        </Popconfirm>
                    ) : null}
                    {r.mode === 'wallet' && r.state === 'live' ? (
                        <Tooltip title={t('Issue now')}><Button type="link" size="small" icon={<SendOutlined />} onClick={() => issueNow(r)} /></Tooltip>
                    ) : null}
                    <Tooltip title={t('Edit')}><Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
                    <Popconfirm title={t('Copy to {{year}} as a draft?', { year: (r.year || dayjs(r.starts_at).year()) + 1 })} onConfirm={() => act('clone', r)}>
                        <Tooltip title={t('Clone to next year')}><Button type="link" size="small" icon={<CopyOutlined />} /></Tooltip>
                    </Popconfirm>
                    {r.mode === 'wallet' ? (
                        <Tooltip title={t('History')}><Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => setHistory(r)} /></Tooltip>
                    ) : null}
                    {r.status === 'draft' && !(r.stats?.issued) ? (
                        <Popconfirm title={t('Delete this draft?')} onConfirm={() => act('delete', r)}>
                            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    ) : null}
                </Space>
            ),
        },
    ];

    const linkedCoupon = (linked || []).length ? null : newCoupon;
    const previewOffer = linkedCoupon?.value ? (linkedCoupon.type === 'fixed' ? t('{{value}} off', { value: formatMoney(linkedCoupon.value) }) : t('{{value}}% off', { value: linkedCoupon.value })) : null;

    return (
        <>
            <HolidayStrip t={t} holidays={holidays} onCreate={openPreset} />
            <Space style={{ marginBottom: 12 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>{t('New campaign')}</Button>
                <Button icon={<ReloadOutlined />} onClick={load} aria-label={t('Refresh')} />
            </Space>
            <Table rowKey="id" size="middle" loading={loading} dataSource={rows} columns={columns} pagination={{ pageSize: 20 }} scroll={{ x: 'max-content' }} />

            <Modal
                open={!!editing}
                title={editing?.id ? t('Edit campaign') : t('New campaign')}
                onCancel={() => setEditing(null)}
                onOk={submit}
                confirmLoading={saving}
                width={720}
                destroyOnClose
            >
                <Form form={form} layout="vertical" requiredMark={false}>
                    <Form.Item name="holiday_key" hidden><Input /></Form.Item>
                    <Form.Item name="year" hidden><InputNumber /></Form.Item>
                    <Form.Item name="name" label={t('Name')} rules={[{ required: true, message: t('Required') }]}><Input maxLength={100} /></Form.Item>
                    <Space size={12} wrap style={{ width: '100%' }}>
                        <Form.Item name="window" label={t('Window')} rules={[{ required: true, message: t('Required') }]}>
                            <RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" />
                        </Form.Item>
                        <Form.Item name="theme" label={t('Theme')}>
                            <Select style={{ width: 200 }} options={Object.entries(CAMPAIGN_THEMES).map(([k, v]) => ({
                                value: k, label: <Space size={6}><span className="cps" style={themeVars(k)} />{t(v.label)}</Space>,
                            }))} />
                        </Form.Item>
                    </Space>
                    <Form.Item name="mode" label={t('Coupon delivery')} tooltip={t('Public code: shown on the banner, users type it. Wallet: issued once to every eligible user.')}>
                        <Radio.Group disabled={editing?.status === 'active'} options={[{ value: 'code', label: t('Public code') }, { value: 'wallet', label: t('Wallet') }]} optionType="button" />
                    </Form.Item>
                    <Form.Item name="coupon_ids" label={t('Linked coupons')}>
                        <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={couponOptions} placeholder={t('Existing coupons')} />
                    </Form.Item>
                    {!editing?.id ? (
                        <Collapse size="small" defaultActiveKey={editing?.preset ? ['nc'] : []} style={{ marginBottom: 16 }} items={[{
                            key: 'nc', label: t('New coupon'), children: (
                                <Space size={12} wrap align="start">
                                    <Form.Item name={['new_coupon', 'code']} label={t('Code')} normalize={(v) => (v || '').toUpperCase()}><Input style={{ width: 160 }} /></Form.Item>
                                    <Form.Item name={['new_coupon', 'type']} label={t('Type')}>
                                        <Select style={{ width: 120 }} options={[{ value: 'percent', label: t('Percent') }, { value: 'fixed', label: t('Fixed amount') }]} />
                                    </Form.Item>
                                    <Form.Item name={['new_coupon', 'value']} label={t('Value')}><InputNumber min={0.01} style={{ width: 100 }} /></Form.Item>
                                    <Form.Item name={['new_coupon', 'applies_to']} label={t('Product line')}>
                                        <Select allowClear style={{ width: 150 }} placeholder={t('All products')} options={productTypes.map((p) => ({ value: p.code, label: p.name || p.code }))} />
                                    </Form.Item>
                                    <Form.Item name={['new_coupon', 'name']} hidden><Input /></Form.Item>
                                </Space>
                            ),
                        }]} />
                    ) : null}
                    <Space size={24} wrap>
                        <Form.Item name="recurring" label={t('Every year')} valuePropName="checked" tooltip={t('When it ends, next year’s draft is created (not started).')}>
                            <Switch />
                        </Form.Item>
                        {mode === 'wallet' ? (
                            <Form.Item name="include_new_users" label={t('Include new users')} valuePropName="checked" tooltip={t('Also issue to users who register during the campaign')}>
                                <Switch />
                            </Form.Item>
                        ) : null}
                    </Space>
                    <Collapse size="small" style={{ marginBottom: 16 }} items={[{
                        key: 'tx', label: t('Banner text and translations'), children: (
                            <>
                                <Form.Item name="banner_text" label={t('Banner text')} tooltip={t('Empty = the coupon value')}><Input maxLength={255} /></Form.Item>
                                {LANGS.map(([l, n]) => (
                                    <Space key={l} size={8} style={{ display: 'flex' }} align="start">
                                        <Form.Item name={['name_i18n', l]} label={`${t('Name')} · ${n}`} style={{ flex: 1, minWidth: 200 }}><Input maxLength={100} /></Form.Item>
                                        <Form.Item name={['banner_i18n', l]} label={`${t('Banner text')} · ${n}`} style={{ flex: 1, minWidth: 200 }}><Input maxLength={255} /></Form.Item>
                                    </Space>
                                ))}
                            </>
                        ),
                    }]} />
                    <ThemePreview t={t} theme={theme} name={name} mode={mode} code={mode === 'code' ? (newCoupon?.code || couponOptions.find((o) => o.value === (linked || [])[0])?.label?.split(' · ')[0]) : null}
                        offer={bannerText || previewOffer} coupon={linkedCoupon?.value ? linkedCoupon : null} />
                </Form>
            </Modal>

            <CampaignRuns t={t} campaign={history} onClose={() => setHistory(null)} />
        </>
    );
};

const CampaignRuns = ({ t, campaign, onClose }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!campaign) return;
        setLoading(true);
        couponService.getRuleRuns({ job: 'campaign', campaign_id: campaign.id, limit: 50 })
            .then((r) => r.success && setRows(r.data || [])).finally(() => setLoading(false));
    }, [campaign]);
    return (
        <Drawer title={`${t('History')} · ${campaign?.name || ''}`} open={!!campaign} onClose={onClose} width={640} destroyOnClose>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} pagination={false}
                columns={[
                    { title: t('Started'), dataIndex: 'started_at', render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss') },
                    { title: t('Source'), dataIndex: 'source', render: (v) => (v === 'manual' ? <Tag color="blue">{t('Manual')}</Tag> : t('Scheduled')) },
                    { title: t('Candidates'), dataIndex: 'candidates' },
                    { title: t('Issued'), dataIndex: 'issued' },
                    { title: t('Error'), dataIndex: 'error', render: (v) => (v ? <Text type="danger" ellipsis={{ tooltip: v }} style={{ maxWidth: 200 }}>{v}</Text> : '') },
                ]} />
        </Drawer>
    );
};

export default CouponCampaigns;
